import React from 'react'
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CompressOutlined,
    EyeOutlined,
    ForkOutlined,
    LogoutOutlined,
    NumberOutlined,
    PlayCircleOutlined,
    ReadOutlined,
    SaveOutlined,
    ShoppingCartOutlined,
    SplitCellsOutlined,
    WifiOutlined,
} from '@ant-design/icons'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import {
    StepType,
    TriggerType,
    CART_ABANDONMENT_TRIGGERS,
    VIEW_ABANDONMENT_TRIGGERS,
    SAVED_ITEM_TRIGGERS,
    COMPLETED_ITEM_TRIGGERS,
} from '@pushly/models/lib/enums/journeys'
import { RecommendedNotificationFilled } from '../../components/icons/recommended-notification'
import {
    AddStepType,
    isDelayStep,
    isActionStep,
    isVariantSplitStep,
    isSendActionOfType,
    getItemStrategy,
} from './types/journey-nodes'
import type { IJourneyStep, IActionStep } from '@pushly/models/lib/structs/journeys'
import { convertSeconds } from '../../_utils/time'
import { RelativeDateDisplayMetric } from '@pushly/aqe/lib/enums'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { BLUECONIC_DISPLAY_VALUE } from './editors/send-recommended-notification-editor/blueconic/constants'
import { VESPA_DISPLAY_VALUE } from './editors/send-recommended-notification-editor/vespa/constants'
import { ITEM_RANKING_STRATEGY_OPTIONS } from '@pushly/models/lib/enums/catalogs/item-ranking-strategy'
import { titleCase } from '../../_utils/utils'
import {
    CatalogItemType,
    getPluralForCatalogItemType,
    getSingularForCatalogItemType,
    getCompletedVerbForCatalogItemType,
    getCompletedPastTenseVerbForCatalogItemType,
    getCompletedNounForCatalogItemType,
    getCompletedPluralNounForCatalogItemType,
} from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { FEAT_JOURNEY_FEED_NOTIFICATION_STEP } from '../../constants'

// region Step Categories

export enum StepCategory {
    TIMING = 'timing',
    NOTIFICATIONS = 'notifications',
    FLOW_CONTROL = 'flow-control',
}

export interface IStepCategoryConfig {
    label: string
    iconClass: string
    order: number
}

export const STEP_CATEGORIES: Record<StepCategory, IStepCategoryConfig> = {
    [StepCategory.TIMING]: {
        label: 'Timing',
        iconClass: 'timing',
        order: 0,
    },
    [StepCategory.NOTIFICATIONS]: {
        label: 'Notifications',
        iconClass: 'notification',
        order: 1,
    },
    [StepCategory.FLOW_CONTROL]: {
        label: 'Flow Control',
        iconClass: 'flow-control',
        order: 2,
    },
}

// endregion

export function formatNodeCategoryEyebrow(label: string): string {
    const upper = label.toUpperCase()
    return upper.endsWith('S') ? upper.slice(0, -1) : upper
}

// region Helper Functions

/**
 * Get step configuration for a journey node.
 * Handles both ACTION steps (uses configuration.type) and other steps (uses type).
 */
export const getStepConfig = (node: { type: string; configuration?: { type?: string } }) => {
    const stepType = node.type
    const key = stepType === StepType.ACTION ? node.configuration?.type : stepType
    return key ? STEP_CONFIG[key] : undefined
}

// endregion

// region Shared Subtitle Helpers

/**
 * Subtitle for action steps that use item_strategy.
 * Uses the typed `getItemStrategy` helper — no casts needed.
 */
function getItemStrategySubtitle(node: IJourneyStep): string {
    if (!isActionStep(node)) return ''
    const strategy = getItemStrategy(node)
    return strategy ? titleCase(strategy) : ''
}

/**
 * Subtitle for ranked item steps — resolves strategy to its display label
 * from ITEM_RANKING_STRATEGY_OPTIONS when available.
 */
function getRankedItemSubtitle(node: IJourneyStep): string {
    if (!isActionStep(node)) return ''
    const strategy = getItemStrategy(node)
    const option = strategy ? ITEM_RANKING_STRATEGY_OPTIONS.find((o) => o.value === strategy) : undefined
    return option?.label ?? (strategy ? titleCase(strategy) : '')
}

/**
 * Subtitle for the send notification action — shows the notification title.
 */
function getNotificationTitleSubtitle(node: IJourneyStep): React.ReactNode {
    if (!isActionStep(node) || !isSendActionOfType(node, JourneyActionType.SEND_NOTIFICATION)) return null
    const notification = node.configuration.params.notification
    return notification?.template?.channels?.default?.title ?? ''
}

/**
 * Subtitle for the recommended notification action — shows the recommendation source.
 */
function getRecommendationSourceSubtitle(node: IJourneyStep): string {
    if (!isActionStep(node) || !isSendActionOfType(node, JourneyActionType.SEND_RECOMMENDED_NOTIFICATION)) return ''
    const source = node.configuration.params.recommendation_source
    let sourceDisplayValue = 'Unspecified'

    switch (source) {
        case RecommendationSource.BLUECONIC:
            sourceDisplayValue = BLUECONIC_DISPLAY_VALUE
            break
        case RecommendationSource.VESPA:
            sourceDisplayValue = VESPA_DISPLAY_VALUE
            break
        case RecommendationSource.UNKNOWN:
        default:
            break
    }

    return `Source: ${sourceDisplayValue}`
}

/**
 * Subtitle for the feed notification action — shows the selected feed's name when
 * resolvable (post-enrichment), otherwise a placeholder.
 *
 * The feed name is not part of the persisted `SendFeedNotificationParams` (which
 * stores only `feed_id`); it is hydrated onto the step by the node enrichment
 * pattern. Until enrichment resolves the feed, `feed` is absent and this falls
 * back to a placeholder — hence the optional chain on the shared type's `feed`.
 */
function getFeedStepSubtitle(node: IJourneyStep): string {
    if (!isActionStep(node) || !isSendActionOfType(node, JourneyActionType.SEND_FEED_NOTIFICATION)) return ''
    const feedName = node.configuration.params.feed?.name
    return feedName ? `Feed: ${feedName}` : 'Feed: —'
}

// endregion

// region Step Configuration

// System steps (TRIGGER, EXIT) - minimal config
interface ISystemStepConfig {
    kind: 'system'
    label: string
    icon: React.ReactNode
    getSubtitle?: (node: IJourneyStep) => React.ReactNode
}

// Declarative availability rules for user-addable steps.
// Omitted or undefined fields impose no restriction.
export interface IStepAvailability {
    /** false = cannot appear inside any branch scope (split or condition). Default: true */
    allowInsideBranch?: boolean
    /** Only show when the journey's trigger type is in this list. */
    triggerTypes?: TriggerType[]
    /** Only show when the domain has an ecomm item type configured. */
    requiresItemType?: boolean
    /** Only show when the domain has an active recommendation integration. */
    requiresRecommendationIntegration?: boolean
    /**
     * Only show when the active domain has this feature flag enabled
     * (`domain.flags.includes(flag)`). Used to gate an add-step entry behind a
     * rollout flag. This restricts only the ADD entry — steps of this type that
     * already exist in a journey still render, edit, and validate normally.
     */
    requiresFlag?: string
}

// User-addable steps - full config
interface IUserStepConfig {
    kind: 'user'
    type: AddStepType
    category: StepCategory
    label: string | ((itemType: CatalogItemType) => string)
    description: string | ((itemType: CatalogItemType) => string)
    icon: React.ReactNode
    getSubtitle?: (node: IJourneyStep) => React.ReactNode
    /** When omitted, the step is always available. */
    availability?: IStepAvailability
}

/** Resolve a step label to a string, calling it with itemType if it's a function. */
export function resolveLabel(
    config: { label: string | ((itemType: CatalogItemType) => string) },
    itemType: CatalogItemType,
): string {
    return typeof config.label === 'function' ? config.label(itemType) : config.label
}

/** Resolve a step description to a string, calling it with itemType if it's a function. */
export function resolveDescription(config: IUserStepConfig, itemType: CatalogItemType): string {
    return typeof config.description === 'function' ? config.description(itemType) : config.description
}

// Union type ensures correct properties for each step type
export type IStepConfig = ISystemStepConfig | IUserStepConfig

// All step types that must have an entry in STEP_CONFIG.
// Adding a new member to AddStepType will cause a compile error here
// until a corresponding config entry is added.
type SystemStepType = StepType.TRIGGER | StepType.EXIT | StepType.PLACEHOLDER
type StepConfigKey = AddStepType | SystemStepType

export const STEP_CONFIG: Record<string, IStepConfig> = {
    // System steps (always present, not user-addable)
    [StepType.TRIGGER]: {
        kind: 'system',
        label: 'Entry',
        icon: <PlayCircleOutlined />,
    },
    [StepType.EXIT]: {
        kind: 'system',
        label: 'Exit',
        icon: <LogoutOutlined />,
    },
    [StepType.PLACEHOLDER]: {
        kind: 'system',
        label: 'Placeholder',
        icon: <CompressOutlined />,
    },
    // User-addable steps
    [StepType.DELAY]: {
        kind: 'user',
        type: StepType.DELAY,
        category: StepCategory.TIMING,
        label: 'Wait',
        description: 'Pause the journey for a specific amount of time.',
        icon: <ClockCircleOutlined />,
        getSubtitle: (node) => {
            if (!isDelayStep(node)) return null

            // Added safety checks for malformed configurations
            const params = node.configuration?.params || {}
            const qualifier = params.qualifier?.toUpperCase() || 'DAYS'
            const delaySeconds = params.delay_seconds || 0

            const displayDelay = convertSeconds(delaySeconds, RelativeDateDisplayMetric[qualifier])
            return `${displayDelay} ${params.qualifier || qualifier.toLowerCase()}`
        },
    },
    [StepType.VARIANT_SPLIT]: {
        kind: 'user',
        type: StepType.VARIANT_SPLIT,
        category: StepCategory.FLOW_CONTROL,
        label: 'Split Test',
        description: 'Route users into multiple variant groups to compare performance.',
        icon: <SplitCellsOutlined />,
        availability: { allowInsideBranch: false },
        getSubtitle: (node) => {
            const variantCount = isVariantSplitStep(node) ? (node.configuration?.branches?.length ?? 0) : 0
            return variantCount > 0 ? `${variantCount} variant${variantCount !== 1 ? 's' : ''}` : null
        },
    },
    [JourneyActionType.SEND_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: 'Custom Notification',
        description: 'Send a fully custom notification built from scratch.',
        icon: <WifiOutlined />,
        getSubtitle: getNotificationTitleSubtitle,
    },
    [JourneyActionType.SEND_CART_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_CART_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: 'Abandoned Cart',
        description: (itemType) =>
            `Send a reminder about unpurchased ${getPluralForCatalogItemType(itemType)} left in the cart.`,
        icon: <ShoppingCartOutlined />,
        availability: { triggerTypes: [...CART_ABANDONMENT_TRIGGERS] },
        getSubtitle: getItemStrategySubtitle,
    },
    [JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: (itemType) => `Saved ${titleCase(getSingularForCatalogItemType(itemType))}`,
        description: (itemType) => `Send a reminder about ${getPluralForCatalogItemType(itemType)} the user has saved.`,
        icon: <SaveOutlined />,
        availability: { triggerTypes: [...SAVED_ITEM_TRIGGERS] },
        getSubtitle: getItemStrategySubtitle,
    },
    [JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: (itemType) => `Recently Viewed ${titleCase(getSingularForCatalogItemType(itemType))}`,
        description: (itemType) =>
            `Send a follow-up message about a recently viewed ${getSingularForCatalogItemType(itemType)}.`,
        icon: <EyeOutlined />,
        availability: { triggerTypes: [...VIEW_ABANDONMENT_TRIGGERS] },
        getSubtitle: getItemStrategySubtitle,
    },
    [JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: (itemType) => `Trending ${titleCase(getSingularForCatalogItemType(itemType))}`,
        description: (itemType) =>
            `Send a notification highlighting top-performing ${getPluralForCatalogItemType(itemType)}.`,
        icon: <NumberOutlined />,
        availability: { requiresItemType: true },
        getSubtitle: getRankedItemSubtitle,
    },
    [JourneyActionType.SEND_COMPLETED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_COMPLETED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: (itemType) =>
            `${titleCase(getCompletedVerbForCatalogItemType(itemType))} ${titleCase(
                getCompletedNounForCatalogItemType(itemType),
            )}`,
        description: (itemType) =>
            `Send a notification about a recently ${getCompletedPastTenseVerbForCatalogItemType(
                itemType,
            )} ${getSingularForCatalogItemType(itemType)}.`,
        icon: <CheckCircleOutlined />,
        availability: { triggerTypes: [...COMPLETED_ITEM_TRIGGERS] },
        getSubtitle: getItemStrategySubtitle,
    },
    [JourneyActionType.SEND_RECOMMENDED_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_RECOMMENDED_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: 'Recommendation',
        description: 'Send a personalized content recommendation to the user.',
        icon: <RecommendedNotificationFilled fill="white" className="p-icon-recommended-notification" />,
        availability: { requiresRecommendationIntegration: true },
        getSubtitle: getRecommendationSourceSubtitle,
    },
    [JourneyActionType.SEND_FEED_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_FEED_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        label: 'Feed Notification',
        description: 'Send an article resolved from a feed.',
        icon: <ReadOutlined />,
        // Gated OFF by default until the backend send path is verified in dev.
        // Only hides the ADD entry — existing feed steps still render/edit/validate.
        availability: { requiresFlag: FEAT_JOURNEY_FEED_NOTIFICATION_STEP },
        getSubtitle: getFeedStepSubtitle,
    },
} satisfies Record<StepConfigKey, IStepConfig>

// endregion
